import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import OperationCaptureDetails from "./OperationCaptureDetails.jsx";
import ConsolidationCaptureDetails from "./ConsolidationCaptureDetails.jsx";
import TruckSequenceComparison from "./TruckSequenceComparison.jsx";
import PlanningOrderEditCaptureDetails from "./PlanningOrderEditCaptureDetails.jsx";
import {
  Check,
  Copy,
  Filter,
  History,
  LoaderCircle,
  RefreshCcw,
  Trash2,
} from "lucide-react";
import {
  clearReassignmentDiagnosticsHistory,
  fetchReassignmentDiagnosticsHistory,
  fetchReassignmentDiagnosticsHistoryConfig,
} from "../lib/systemConfigApi.js";
import {
  buildReassignmentHistoryDiagnosticPanel,
  buildReassignmentHistoryItemCopyText,
  buildSourceChangesPerformanceCardMetrics,
  filterReassignmentDiagnosticHistoryItems,
  formatReassignmentHistoryActionLabel,
  listPlanningDiagnosticActionOptions,
  resolvePlanningDiagnosticMetricEmphasis,
  normalizeReassignmentDiagnosticsHistoryPayload,
} from "../utils/planningLastDiagnostics.js";
import useDispatchCaptureArrivalHighlight from "../hooks/useDispatchCaptureArrivalHighlight.js";
import { REASSIGNMENT_DIAGNOSTICS_HISTORY_POLL_INTERVAL_MS } from "../hooks/useReassignmentDiagnosticsHistoryActive.js";
import {
  buildReassignmentCaptureKey,
  DISPATCH_CAPTURE_ARRIVAL_CLASS_NAME,
} from "../lib/dispatchCaptureArrivalHighlight.js";
import { publishDispatchCaptureArrival, REASSIGNMENT_DIAGNOSTICS_STATUS_EVENT, subscribeDispatchCaptureStatus } from "../lib/dispatchCaptureStatusEvents.js";
import {
  formatCaptureActivityGroupLabel,
  groupCaptureItemsByActivityDate,
} from "../utils/dispatchCaptureListGroups.js";
import { resolvePageSurfaceWidthClassName } from "../utils/pageSurfaceLayout.js";
import ConfirmationModal from "./ConfirmationModal.jsx";
import DispatchCaptureDispatchGroupHeader from "./DispatchCaptureDispatchGroupHeader.jsx";
import DispatchCaptureHistorySwitcher from "./DispatchCaptureHistorySwitcher.jsx";
import WorkspacePageHeader from "./WorkspacePageHeader.jsx";
import { MetricCard } from "./ResultsPrimitives.jsx";
import {
  buttonSizeClasses,
  buttonToneClasses,
  joinClasses,
  pillToneClasses,
  statusToneClasses,
} from "./resultsPrimitives/shared.js";

const SYSTEM_CONFIG_SURFACE_WIDTH_CLASS_NAME = resolvePageSurfaceWidthClassName("system_config");
const PAGE_SIZE = 25;
const CONSOLIDATION_DESCRIPTION = "Sauvegardes, validations, préchargements, heures Geotab, chauffeurs et changements du Dispatch de la page Consolidation.";
const EMPTY_FILTERS = {
  action: "",
  dispatchDate: "",
  status: "",
};
const SECONDARY_ACTION_CLASS_NAME = joinClasses([
  "inline-flex items-center justify-center gap-2 rounded-[4px] border font-semibold transition disabled:cursor-not-allowed disabled:opacity-60",
  buttonSizeClasses.sm,
  buttonToneClasses.ghost,
]);
const COMPACT_PRIMARY_ACTION_CLASS_NAME = joinClasses([
  "inline-flex items-center justify-center gap-1.5 rounded-[4px] border font-semibold transition disabled:cursor-not-allowed disabled:opacity-60",
  buttonSizeClasses.pill,
  "border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong focus-visible:ring-2 focus-visible:ring-accent",
]);
const COMPACT_SECONDARY_ACTION_CLASS_NAME = joinClasses([
  "inline-flex items-center justify-center gap-1.5 rounded-[4px] border font-semibold transition disabled:cursor-not-allowed disabled:opacity-60",
  buttonSizeClasses.pill,
  buttonToneClasses.ghost,
]);
const COMPACT_DANGER_ACTION_CLASS_NAME = joinClasses([
  "inline-flex items-center justify-center gap-1.5 rounded-[4px] border font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-60",
  buttonSizeClasses.pill,
  "border-rose-600 bg-rose-600 hover:bg-rose-700",
]);
const RUN_TOTAL_DURATION_CARD_CLASS_NAME = "w-[7.5rem]! min-w-[7.5rem]! max-w-[7.5rem]! justify-self-start! gap-0! rounded-[4px]! px-2! py-1! text-center!";
const RUN_TOTAL_DURATION_FALLBACK_CLASS_NAME = "inline-flex w-[7.5rem] min-w-[7.5rem] max-w-[7.5rem] items-center justify-center rounded-[4px] border-2 px-2.5 py-1 text-[0.74rem] font-semibold";
const RUN_TOTAL_DURATION_GRID_CLASS_NAME = "grid w-full grid-cols-[7.5rem_minmax(0,1fr)] items-start gap-1";
const PRIMARY_TOTAL_METRIC_KEYS = new Set([
  "run-total-backend",
  "reassignment-total-backend",
  "source-changes-operation-total",
]);

function resolveHistoryPrimaryMetricIndex(metrics = []) {
  const primaryIndex = metrics.findIndex((metric) => PRIMARY_TOTAL_METRIC_KEYS.has(metric?.key));
  return primaryIndex >= 0 ? primaryIndex : 0;
}

function formatDurationLabel(totalMs) {
  if (!Number.isFinite(totalMs)) {
    return "n/d";
  }
  if (totalMs >= 60000) {
    const totalSeconds = Math.round(totalMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return seconds ? `${minutes} min ${seconds} s` : `${minutes} min`;
  }
  if (totalMs >= 1000) {
    return `${(totalMs / 1000).toLocaleString("fr-CA", { maximumFractionDigits: 2 })} s`;
  }
  return `${totalMs.toLocaleString("fr-CA", { maximumFractionDigits: 1 })} ms`;
}

function HistoryMetricGrid({ metrics = [], status = "" }) {
  const visibleMetrics = (Array.isArray(metrics) ? metrics : []).filter(Boolean);
  if (!visibleMetrics.length) {
    return null;
  }

  const metricEmphasis = resolvePlanningDiagnosticMetricEmphasis(status);
  const emphasizedBorderClass = metricEmphasis === "problem"
    ? "border-2! border-[var(--danger)]!"
    : metricEmphasis === "warning"
      ? "border-2! border-amber!"
      : metricEmphasis === "success"
        ? "border-2! border-accent!"
        : "border-2! border-slate-800!";
  const primaryMetricIndex = resolveHistoryPrimaryMetricIndex(visibleMetrics);
  const primaryMetric = visibleMetrics[primaryMetricIndex];
  const secondaryMetrics = visibleMetrics.filter((_, metricIndex) => metricIndex !== primaryMetricIndex);

  const renderMetricCard = (metric, { isPrimaryTotal = false } = {}) => {
    const isEmphasizedMetric = isPrimaryTotal;
    return (
      <MetricCard
        key={metric.key || metric.label}
        data-operation-step={metric.operationStep ? "true" : undefined}
        className={isPrimaryTotal
          ? joinClasses([RUN_TOTAL_DURATION_CARD_CLASS_NAME, emphasizedBorderClass])
          : "gap-0! rounded-[4px]! px-1.5! py-0.5!"}
        data-reassignment-diagnostics-metric-emphasis={isEmphasizedMetric ? metricEmphasis : undefined}
        showLabel={!isPrimaryTotal || ["source-changes-operation-total", "ortools-called", "fallback-executed"].includes(metric.key)}
        label={metric.label}
        labelClassName={isEmphasizedMetric
          ? "text-[0.62rem]! tracking-[0.05em] leading-none"
          : "text-[0.5rem]! tracking-[0.05em] leading-none"}
        tone={metric.tone === "accent" ? "neutral" : (metric.tone || "neutral")}
        value={metric.value}
        note={metric.operationStep ? metric.note : ""}
        noteInline={metric.operationStep === true}
        valueClassName={isEmphasizedMetric
          ? joinClasses([
            "text-[1.15rem]! font-bold leading-none!",
            isPrimaryTotal ? "tabular-nums text-center" : "",
          ])
          : "text-[0.7rem]! leading-none!"}
      />
    );
  };

  return (
    <div
      className={RUN_TOTAL_DURATION_GRID_CLASS_NAME}
      data-reassignment-diagnostics-metrics="true"
    >
      {renderMetricCard(primaryMetric, { isPrimaryTotal: true })}
      {secondaryMetrics.length ? (
        <div
          className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(6.75rem,1fr))] gap-1"
          data-reassignment-diagnostics-secondary-metrics="true"
        >
          {secondaryMetrics.map((metric) => renderMetricCard(metric))}
        </div>
      ) : null}
    </div>
  );
}

function HistoryCopyButton({ copyText = "" }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const text = typeof copyText === "string" ? copyText.trim() : "";
    if (!text || typeof navigator === "undefined" || !navigator.clipboard?.writeText) {
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  return (
    <button
      className={SECONDARY_ACTION_CLASS_NAME}
      onClick={() => {
        void handleCopy();
      }}
      type="button"
    >
      {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
      {copied ? "Copié" : "Copier pour analyse"}
    </button>
  );
}

function formatCaptureTimestampDisplayValue(item) {
  const value = typeof item?.value === "string" ? item.value.trim() : "";
  if (!value) {
    return "";
  }
  if (item.key === "performed-time") {
    return value.replace(/:\d{2}$/, "");
  }
  if (item.key === "dispatch-date" || item.key === "delivery-date") {
    const withoutYear = value.replace(/,?\s*\d{4}(?=,|\s*$)/g, "").trim();
    return withoutYear || value;
  }
  return value;
}

function isEmphasizedCaptureTimestamp(item) {
  return item?.key === "performed-time" || item?.key === "dispatch-date" || item?.key === "delivery-date";
}

export function ReassignmentHistoryCard({ isNew = false, item }) {
  const actionLabel = formatReassignmentHistoryActionLabel(item);
  const panel = buildReassignmentHistoryDiagnosticPanel(item);
  const timestampItems = Array.isArray(panel?.timestampItems) ? panel.timestampItems.filter(Boolean) : [];
  const contextItems = Array.isArray(panel?.contextItems) ? panel.contextItems.filter(Boolean) : [];
  const pills = Array.isArray(panel?.pills) ? panel.pills.filter(Boolean) : [];
  const metrics = Array.isArray(panel?.metrics) ? panel.metrics.filter(Boolean) : [];
  const performanceMetrics = buildSourceChangesPerformanceCardMetrics(item);
  const feedback = item?.diagnostics?.operation_feedback;
  const isGanttEdit = feedback?.action === "gantt_source_backed_save";
  // Geste Consolidation instantané (brouillon, ouverture, annulation) : sans durée à mesurer.
  const consolidation = feedback?.consolidation_context;
  const instant = Boolean(consolidation && (consolidation.draft || ["opened", "cancelled"].includes(consolidation.outcome)));
  const isTruckOptimization = metrics.some((metric) => metric.key === "ortools-called");
  const visibleMetrics = [...(performanceMetrics.length ? performanceMetrics : metrics),
    ...(isGanttEdit && performanceMetrics.length ? metrics.filter((metric) => metric.key !== "reassignment-click-to-visible") : []),
    ...(isTruckOptimization && performanceMetrics.length
      ? metrics.filter((metric) => ["reassignment-local-solver", "ortools-called", "fallback-executed", "ortools-routing"].includes(metric.key)) : []),
    ...(feedback?.steps || []).map((step) => ({
    key: `operation-step-${step.key}`, operationStep: true, label: step.label,
    tone: step.status === "failed" ? "danger" : step.status === "warning" ? "warning" : "neutral",
    value: step.duration_ms == null ? "Non exécutée" : formatDurationLabel(step.duration_ms),
    note: ({ completed: "Terminée", warning: "Avertissement", failed: "Échec", skipped: "Ignorée" })[step.status],
  }))];
  const copyText = buildReassignmentHistoryItemCopyText(item);
  const metricEmphasis = resolvePlanningDiagnosticMetricEmphasis(item?.status);
  const statusTone = item?.statusTone || "info";
  const statusLabel = item?.statusLabel || "Mesuré";

  return (
    <article
      className={joinClasses([
        "grid gap-1.5 rounded-[4px] border border-black/6 bg-white px-2.5 py-2 shadow-[0_10px_24px_rgba(15,23,42,0.04)]",
        isNew ? DISPATCH_CAPTURE_ARRIVAL_CLASS_NAME : "",
      ])}
      data-dispatch-capture-arrival={isNew ? "true" : undefined}
      data-reassignment-diagnostics-card="true"
    >
      <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
          <strong className="min-w-0 max-w-full break-words text-[0.86rem] tracking-[-0.02em] text-slate-950">
            {actionLabel || "Réaffectation"}
          </strong>
          {timestampItems.length ? (
            <p
              className="m-0 flex min-w-0 flex-1 basis-full flex-wrap items-baseline gap-x-1.5 gap-y-0 text-[0.62rem] font-medium leading-4 text-slate-500 sm:basis-0"
              data-reassignment-diagnostics-timestamps="true"
            >
              {timestampItems.map((timestampItem, index) => {
                const isEmphasized = isEmphasizedCaptureTimestamp(timestampItem);
                const displayValue = formatCaptureTimestampDisplayValue(timestampItem);
                return (
                  <span className="inline-flex min-w-0 items-baseline gap-1.5" key={timestampItem.key}>
                    {index > 0 ? <span aria-hidden="true" className="text-[0.52rem] text-slate-300">·</span> : null}
                    <span
                      className={timestampItem.key === "delivery-date"
                        ? "inline-flex min-w-0 flex-wrap items-baseline gap-1"
                        : isEmphasized ? "inline-flex items-baseline gap-1 whitespace-nowrap" : "truncate"}
                      title={`${timestampItem.label} : ${timestampItem.value}`}
                    >
                      <span className="text-slate-400">{timestampItem.label}</span>
                      {" "}
                      <span
                        className={isEmphasized
                          ? "text-[0.92rem] font-bold leading-none text-slate-950"
                          : "text-slate-600"}
                      >
                        {timestampItem.key === "delivery-date"
                          ? displayValue.split(", ").map((date, dateIndex) => (
                            <span className="inline-block whitespace-nowrap" key={date}>
                              {dateIndex > 0 ? ", " : ""}{date}
                            </span>
                          ))
                          : displayValue}
                      </span>
                    </span>
                  </span>
                );
              })}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          <span
            className={joinClasses([
              "inline-flex items-center rounded-[4px] px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-[0.06em]",
              statusToneClasses[statusTone] || statusToneClasses.info,
            ])}
            data-reassignment-diagnostics-status="true"
          >
            {statusLabel}
          </span>
          <HistoryCopyButton copyText={copyText} />
        </div>
      </div>

      {feedback ? (
        <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-1" data-operation-feedback="true">
          {consolidation ? <ConsolidationCaptureDetails feedback={feedback} /> : !isGanttEdit ? <OperationCaptureDetails feedback={feedback} /> : null}
          <p className="m-0 min-w-0 whitespace-pre-wrap text-xs text-slate-600 [overflow-wrap:anywhere]"><strong>{feedback.toast_title}</strong>{" · "}{feedback.toast_message}</p>
          {feedback.toast_truncated || feedback.resource_ids_truncated ? <p className="m-0 text-xs text-slate-600">{feedback.toast_truncated ? "Texte tronqué. " : ""}{feedback.resource_ids_truncated ? "Liste de ressources partielle." : ""}</p> : null}
        </div>
      ) : null}

      <PlanningOrderEditCaptureDetails item={item} />

      {contextItems.length ? (
        <div className="grid gap-1 md:grid-cols-3">
          {contextItems.map((contextItem) => {
            const isOrderItem = contextItem.key === "order";
            const shouldWrapDetail = contextItem.detailWrap === true;
            return (
              <div
                className="grid min-w-0 gap-0 rounded-[4px] border border-slate-200 bg-slate-50/80 px-2 py-1"
                key={contextItem.key}
              >
                <span className="text-[0.52rem] font-extrabold uppercase tracking-[0.07em] text-slate-500">{contextItem.label}</span>
                {isOrderItem && contextItem.detail ? (
                  <strong className="min-w-0 whitespace-normal break-words text-[0.76rem] leading-tight text-slate-950" title={contextItem.detail}>
                    {contextItem.detail}
                  </strong>
                ) : null}
                <strong
                  className={`min-w-0 text-[0.76rem] leading-tight text-slate-950 ${isOrderItem ? "whitespace-normal break-words" : "truncate"}`}
                  title={contextItem.value}
                >
                  {contextItem.value}
                </strong>
                {!isOrderItem && contextItem.detail ? (
                  <span
                    className={shouldWrapDetail
                      ? "min-w-0 whitespace-pre-line break-words text-[0.6rem] leading-4 text-slate-500"
                      : "min-w-0 truncate text-[0.6rem] leading-4 text-slate-500"}
                    title={contextItem.detail}
                  >
                    {contextItem.detail}
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {pills.length ? (
        <div className="flex flex-wrap gap-1">
          {pills.map((pill) => (
            <span
              className={joinClasses([
                "inline-flex items-center rounded-[4px] px-2 py-0.5 text-[0.56rem] font-bold uppercase tracking-[0.08em]",
                pill.tone === "warning" ? pillToneClasses.warning : pillToneClasses.success,
              ])}
              key={pill.key}
            >
              {pill.label}
            </span>
          ))}
        </div>
      ) : null}

      <TruckSequenceComparison item={item} />

      {/* Capture écrite par le serveur : durées seulement une fois complétée par le navigateur (changement du Gantt). */}
      {instant || (feedback?.source === "server_transaction" && feedback.client_total_ms == null) ? null : visibleMetrics.length ? (
        <HistoryMetricGrid
          metrics={visibleMetrics}
          status={item?.status}
        />
      ) : (
        <span
          className={joinClasses([
            RUN_TOTAL_DURATION_FALLBACK_CLASS_NAME,
            metricEmphasis === "problem"
              ? "border-[var(--danger)] bg-danger-soft text-danger"
              : metricEmphasis === "warning"
                ? "border-amber bg-amber-soft text-warning-ink"
                : metricEmphasis === "success"
                  ? "border-accent bg-accent-soft text-accent"
                  : "border-slate-800 bg-slate-50 text-slate-800",
          ])}
          data-reassignment-diagnostics-metric-emphasis={metricEmphasis}
        >
          Total {formatDurationLabel(item.totalMs)}
        </span>
      )}
    </article>
  );
}

export default function ReassignmentDiagnosticsPage({ embedded = false, activityScope = "dispatch" } = {}) {
  const historyTitle = activityScope === "consolidation" ? "Activités Consolidation" : activityScope === "source_changes" ? "Activités Mise à jour" : "Activité Dispatch";
  const captureView = activityScope === "consolidation" || activityScope === "source_changes" ? activityScope : "reassignment";
  const [historyState, setHistoryState] = useState(null);
  const [filterDraft, setFilterDraft] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isClearing, setIsClearing] = useState(false);
  const [isClearConfirmationOpen, setIsClearConfirmationOpen] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [actionFeedback, setActionFeedback] = useState(null);
  const [isHistoryActive, setIsHistoryActive] = useState(true);
  const requestSequenceRef = useRef(0);

  const loadHistory = useCallback(async ({ quiet = false } = {}) => {
    const requestSequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestSequence;
    if (!quiet) {
      setIsLoading(true);
      setLoadError("");
    }
    try {
      const payload = normalizeReassignmentDiagnosticsHistoryPayload(
        await fetchReassignmentDiagnosticsHistory({ activityScope }),
      );
      if (requestSequence !== requestSequenceRef.current) {
        return;
      }
      setHistoryState(payload);
    } catch (error) {
      if (requestSequence !== requestSequenceRef.current) {
        return;
      }
      if (!quiet) {
        setLoadError(error instanceof Error ? error.message : "Impossible de charger l’historique.");
      }
    } finally {
      if (requestSequence === requestSequenceRef.current && !quiet) {
        setIsLoading(false);
      }
    }
  }, [activityScope]);

  const loadHistoryConfig = useCallback(async () => {
    try {
      const payload = await fetchReassignmentDiagnosticsHistoryConfig();
      setIsHistoryActive(payload?.is_active !== false);
    } catch {
      // L’historique reste exploitable même si le statut du cumul n’est pas disponible.
    }
  }, []);

  useEffect(() => {
    void loadHistory();
    void loadHistoryConfig();
  }, [loadHistory, loadHistoryConfig]);

  useEffect(() => subscribeDispatchCaptureStatus(REASSIGNMENT_DIAGNOSTICS_STATUS_EVENT, setIsHistoryActive), []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    let intervalId = null;
    const refreshQuietly = () => {
      if (typeof document === "undefined" || document.visibilityState !== "hidden") {
        void loadHistory({ quiet: true });
        void loadHistoryConfig();
      }
    };
    const startPollingInterval = () => {
      if (intervalId != null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
      if (typeof document === "undefined" || document.visibilityState !== "hidden") {
        intervalId = window.setInterval(refreshQuietly, REASSIGNMENT_DIAGNOSTICS_HISTORY_POLL_INTERVAL_MS);
      }
    };
    const handleVisibilityChange = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        if (intervalId != null) {
          window.clearInterval(intervalId);
          intervalId = null;
        }
        return;
      }
      refreshQuietly();
      startPollingInterval();
    };

    startPollingInterval();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }
    return () => {
      if (intervalId != null) {
        window.clearInterval(intervalId);
      }
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
    };
  }, [loadHistory, loadHistoryConfig]);

  const items = Array.isArray(historyState?.items) ? historyState.items : [];
  const itemKeys = useMemo(
    () => items.map((item) => buildReassignmentCaptureKey(item)),
    [items],
  );
  const highlightedKeys = useDispatchCaptureArrivalHighlight(itemKeys, {
    enabled: historyState != null,
    onArrival: () => {
      publishDispatchCaptureArrival(captureView);
    },
    view: captureView,
  });
  const filteredItems = useMemo(
    () => filterReassignmentDiagnosticHistoryItems(items, appliedFilters),
    [appliedFilters, items],
  );
  const pageCount = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pagedItems = filteredItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const pagedItemsByActivityDate = useMemo(
    () => groupCaptureItemsByActivityDate(
      filteredItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
      (item) => item?.recordedAt,
    ),
    [currentPage, filteredItems],
  );
  const actionOptions = useMemo(() => listPlanningDiagnosticActionOptions(items, activityScope), [items, activityScope]);
  const hasHistory = items.length > 0;

  const updateFilterDraft = (key, value) => {
    setFilterDraft((currentValue) => ({
      ...currentValue,
      [key]: value,
    }));
  };

  const handleApplyFilters = (event) => {
    event.preventDefault();
    setAppliedFilters(filterDraft);
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilterDraft(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const handleClearHistory = useCallback(async () => {
    if (isClearing) {
      return;
    }
    setIsClearing(true);
    try {
      const payload = await clearReassignmentDiagnosticsHistory({ activityScope });
      setIsClearConfirmationOpen(false);
      setActionFeedback({
        message: Number(payload?.deleted_count) > 0
          ? `Historique « ${historyTitle} » effacé.`
          : "Aucun historique à effacer.",
        tone: "success",
      });
      setPage(1);
      await loadHistory();
    } catch (error) {
      setActionFeedback({
        message: error instanceof Error ? error.message : "Impossible d’effacer l’historique.",
        tone: "danger",
      });
    } finally {
      setIsClearing(false);
    }
  }, [isClearing, loadHistory, activityScope, historyTitle]);

  const actionFeedbackClassName = actionFeedback?.tone === "danger"
    ? statusToneClasses.danger
    : statusToneClasses.success;

  return (
    <div
      className={embedded
        ? "grid max-w-full min-h-min min-w-0 gap-3 overflow-x-clip"
        : joinClasses(["mx-auto grid w-full max-w-full min-h-min min-w-0 gap-3 overflow-x-clip px-4 pb-6 pt-0 max-[720px]:px-4", SYSTEM_CONFIG_SURFACE_WIDTH_CLASS_NAME])}
      data-reassignment-diagnostics-page="true"
    >
      {embedded ? null : (
      <WorkspacePageHeader
        icon={History}
        actions={<DispatchCaptureHistorySwitcher active={captureView} />}
        className="sticky top-0 z-30 self-start"
        description={activityScope === "consolidation" ? CONSOLIDATION_DESCRIPTION : activityScope === "source_changes" ? "Opérations Mise à jour et sauvegardes commande/camion/client du Data Manager." : "Mouvements, optimisations et réinitialisations du Dispatch."}
        title={historyTitle}
      />
      )}

      <section className="grid gap-3 rounded-[4px] border border-black/6 bg-white px-4 py-3.5 shadow-[0_14px_34px_rgba(15,23,42,0.05)]">
        <form className="grid gap-2" onSubmit={handleApplyFilters}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className={joinClasses([
                "inline-flex items-center rounded-[4px] px-2.5 py-1 text-[0.65rem] font-bold uppercase tracking-[0.12em]",
                isHistoryActive ? pillToneClasses.success : pillToneClasses.danger,
              ])}>
                {isHistoryActive ? "Actif" : "Inactif"}
              </span>
              <span className="text-[0.8rem] font-medium text-slate-600">
                {filteredItems.length} résultat{filteredItems.length > 1 ? "s" : ""} selon les filtres
                {historyState?.total != null ? ` · ${historyState.total} dans cet historique` : ""}
              </span>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-1.5">
              <button
                className={COMPACT_DANGER_ACTION_CLASS_NAME}
                disabled={isLoading || isClearing || !hasHistory}
                onClick={() => {
                  setActionFeedback(null);
                  setIsClearConfirmationOpen(true);
                }}
                type="button"
              >
                <Trash2 size={13} aria-hidden="true" />
                Effacer l’historique
              </button>
              <button className={COMPACT_SECONDARY_ACTION_CLASS_NAME} onClick={handleResetFilters} type="button">
                Réinitialiser
              </button>
              <button className={COMPACT_PRIMARY_ACTION_CLASS_NAME} disabled={isLoading} type="submit">
                <Filter size={12} aria-hidden="true" />
                Appliquer les filtres
              </button>
            </div>
          </div>

          {actionFeedback ? (
            <p className={joinClasses(["m-0 rounded-[4px] px-3 py-2 text-[0.78rem] font-medium", actionFeedbackClassName])} role="status">
              {actionFeedback.message}
            </p>
          ) : null}

          <div className="grid gap-2 rounded-[4px] border border-slate-200 bg-slate-50/70 p-3">
            <div className="flex items-center gap-2 text-[0.72rem] font-bold uppercase tracking-[0.12em] text-slate-600">
              <Filter size={14} aria-hidden="true" />
              Filtres
            </div>
            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            <label className="grid gap-1 text-[0.7rem] font-semibold text-slate-600">
              Journée de dispatch
              <input
                className="h-9 min-w-0 rounded-[4px] border border-slate-200 bg-white px-3 text-[0.78rem] font-normal text-slate-900 outline-hidden focus:border-border-accent-strong focus:ring-2 focus:ring-border-accent-subtle"
                onChange={(event) => updateFilterDraft("dispatchDate", event.target.value)}
                type="date"
                value={filterDraft.dispatchDate}
              />
            </label>
            <label className="grid gap-1 text-[0.7rem] font-semibold text-slate-600">
              Type d’action
              <select
                className="h-9 min-w-0 rounded-[4px] border border-slate-200 bg-white px-3 text-[0.78rem] font-normal text-slate-900 outline-hidden focus:border-border-accent-strong focus:ring-2 focus:ring-border-accent-subtle"
                onChange={(event) => updateFilterDraft("action", event.target.value)}
                value={filterDraft.action}
              >
                <option value="">Tous les types</option>
                {actionOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label className="grid gap-1 text-[0.7rem] font-semibold text-slate-600">
              Statut
              <select
                className="h-9 min-w-0 rounded-[4px] border border-slate-200 bg-white px-3 text-[0.78rem] font-normal text-slate-900 outline-hidden focus:border-border-accent-strong focus:ring-2 focus:ring-border-accent-subtle"
                onChange={(event) => updateFilterDraft("status", event.target.value)}
                value={filterDraft.status}
              >
                <option value="">Tous les statuts</option>
                <option value="success">Réussi</option>
                <option value="no_change">Aucun changement</option>
                <option value="refused">Refusé / Bloqué</option>
                <option value="awaiting_confirmation">À confirmer</option>
                <option value="partial">Partiel</option>
                <option value="failed">Échec</option>
                <option value="cancelled">Annulé</option>
              </select>
            </label>
          </div>
          </div>
        </form>
      </section>

      {loadError ? (
        <section className="grid gap-3 rounded-[4px] border border-rose-200 bg-white px-5 py-5 shadow-[0_14px_34px_rgba(15,23,42,0.05)]">
          <p className="m-0 text-[0.9rem] font-semibold text-rose-700">{loadError}</p>
          <div>
            <button className={SECONDARY_ACTION_CLASS_NAME} onClick={() => void loadHistory()} type="button">
              <RefreshCcw size={15} aria-hidden="true" />
              Réessayer
            </button>
          </div>
        </section>
      ) : isLoading && !historyState ? (
        <section className="flex min-h-52 items-center justify-center gap-3 rounded-[4px] border border-black/6 bg-white text-[0.86rem] font-medium text-slate-600 shadow-[0_14px_34px_rgba(15,23,42,0.05)]">
          <LoaderCircle className="animate-spin" size={18} aria-hidden="true" />
          Chargement des diagnostics…
        </section>
      ) : pagedItems.length > 0 ? (
        <section
          className="grid max-w-full min-h-min min-w-0 gap-6 overflow-x-clip"
          aria-label={`Historique ${historyTitle}`}
          data-reassignment-diagnostics-history="true"
        >
          {pagedItemsByActivityDate.map((group) => (
            <div
              className="grid max-w-full min-h-min min-w-0 gap-2 overflow-x-clip rounded-[4px] border border-slate-200 bg-slate-50/90 p-2.5"
              data-dispatch-capture-dispatch-group="true"
              key={group.activityDate || "unspecified"}
            >
              <DispatchCaptureDispatchGroupHeader
                count={group.items.length}
                countLabel={`${group.items.length} diagnostic${group.items.length > 1 ? "s" : ""}`}
                label={formatCaptureActivityGroupLabel(group.activityDate)}
              />
              {group.items.map((item, index) => {
                const itemKey = buildReassignmentCaptureKey(item) || `item-${index}`;
                return (
                  <ReassignmentHistoryCard
                    isNew={highlightedKeys.has(itemKey)}
                    item={item}
                    key={itemKey}
                  />
                );
              })}
            </div>
          ))}
        </section>
      ) : (
        <section className="grid min-h-52 place-items-center rounded-[4px] border border-dashed border-slate-300 bg-white px-5 py-8 text-center shadow-[0_14px_34px_rgba(15,23,42,0.04)]">
          <div className="grid max-w-lg gap-2">
            <History className="mx-auto text-slate-400" size={30} aria-hidden="true" />
            <p className="m-0 text-[1rem] font-semibold text-slate-900">{`Aucune activité dans « ${historyTitle} »`}</p>
            <p className="m-0 text-[0.82rem] leading-5 text-slate-600">
              {activityScope === "consolidation" ? "Les gestes de la page Consolidation apparaissent ici lorsque la collecte est active."
                : activityScope === "source_changes" ? "Les opérations Mise à jour et les sauvegardes commande/camion/client apparaissent ici lorsque la collecte est active." : "Les mouvements, optimisations et réinitialisations apparaissent ici lorsque la collecte est active."}
            </p>
          </div>
        </section>
      )}

      {filteredItems.length > 0 ? (
        <nav className="flex flex-wrap items-center justify-between gap-3 rounded-[4px] border border-black/6 bg-white px-4 py-3 shadow-[0_10px_24px_rgba(15,23,42,0.04)]" aria-label="Pagination des diagnostics">
          <span className="text-[0.76rem] font-medium text-slate-600">
            Page {currentPage} sur {pageCount} · {filteredItems.length} résultat{filteredItems.length > 1 ? "s" : ""}
          </span>
          <div className="flex items-center gap-2">
            <button className={SECONDARY_ACTION_CLASS_NAME} disabled={isLoading || currentPage <= 1} onClick={() => setPage((currentValue) => Math.max(1, currentValue - 1))} type="button">
              Précédente
            </button>
            <button
              className={SECONDARY_ACTION_CLASS_NAME}
              disabled={isLoading || currentPage >= pageCount}
              onClick={() => setPage((currentValue) => currentValue + 1)}
              type="button"
            >
              Suivante
            </button>
          </div>
        </nav>
      ) : null}

      <ConfirmationModal
        confirmCheckbox={{
          description: `Cette suppression définitive concerne uniquement « ${historyTitle} », pour toutes les dates et tous les filtres.`,
          key: "confirm-clear-reassignment-diagnostics",
          label: `Je confirme vouloir supprimer l’historique « ${historyTitle} ».`,
          required: true,
        }}
        confirmLabel={isClearing ? "Suppression…" : "Effacer définitivement"}
        description={`Les entrées de « ${historyTitle} » seront supprimées. Les autres historiques seront conservés.`}
        errorMessage={actionFeedback?.tone === "danger" ? actionFeedback.message : ""}
        isOpen={isClearConfirmationOpen}
        onCancel={() => {
          if (!isClearing) {
            setIsClearConfirmationOpen(false);
          }
        }}
        onConfirm={handleClearHistory}
        summaryItems={[
          { label: "Entrées", value: historyTitle, tone: "warning" },
          { label: "Portée", value: "Toutes les dates, filtres ignorés", tone: "neutral" },
        ]}
        title={`Effacer l’historique « ${historyTitle} » ?`}
        tone="danger"
        warningMessage="Les timings supprimés ne pourront pas être récupérés."
      />
    </div>
  );
}
